import React from "react";
import {useLocation, useNavigate} from "react-router-dom";
import { api } from "../../../interceptores/instancias";
import { buildNavigationFromPerms, NavModule } from "../../../config/navigationConfig";
import useStorage from "../../../hooks/storage/useStorage";
import { IRespuestaContexto, IUser} from "../model/modelAuth";
import { decryptData, encryptData} from "../../../utilidades/encrypt";

/**
 * Clave utilizada para almacenar la sesión autenticada.
 */
const STORAGE_KEY = "pandimed-contexto";

/**
 * Header enviado al backend para identificar
 * el contexto activo del usuario.
 */
const CONTEXT_HEADER = "X-Usuario-Rol-Contexto-Id";

interface EstablishSessionOptions {
  /**
   * Indica si la sesión debe almacenarse localmente.
   * Durante la restauración no es necesario guardarla otra vez.
   */
  persist?: boolean;

  /**
   * Indica si debe redirigirse al usuario después
   * de establecer la sesión.
   */
  redirect?: boolean;

  /**
   * Ruta utilizada para determinar el módulo seleccionado
   * durante la restauración de la sesión.
   */
  currentPath?: string;
}

interface UseSessionProps {
  setUser: React.Dispatch< React.SetStateAction<IUser | undefined>>;
  setNavigation: React.Dispatch<React.SetStateAction<NavModule[]>>;
  setSelectedNav: React.Dispatch<React.SetStateAction<NavModule | undefined>>;
}

/**
 * Administra los detalles internos de la sesión autenticada.
 *
 * Responsabilidades:
 * - Guardar y restaurar la sesión.
 * - Configurar el contexto de Axios.
 * - Construir el usuario autenticado.
 * - Construir la navegación según los permisos.
 * - Limpiar la sesión al cerrar sesión.
 */
const useSession = ({ setUser, setNavigation, setSelectedNav}: UseSessionProps) => {
  
  const { saveItem, getItem, deleteItem } =useStorage();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Construye el usuario utilizado por la aplicación
   * a partir de la respuesta del contexto seleccionado.
   */
  const buildUser = React.useCallback(
    (session: IRespuestaContexto): IUser => ({
      id: session.user.id,
      nombre: session.user.nombre,
      email: session.user.email,
      rol: session.rol.rol_nombre,
    }),
    []
  );

  /**
   * Configura en Axios el identificador del contexto
   * activo del usuario.
   */
  const configureApiContext = React.useCallback(
    (session: IRespuestaContexto): void => {
      api.defaults.headers.common[CONTEXT_HEADER] =
        String(
          session.contexto.usuarioRolContextoId
        );
    },
    []
  );

  /**
   * Elimina de Axios el contexto asociado
   * a la sesión anterior.
   */
  const clearApiContext =
    React.useCallback((): void => {
      delete api.defaults.headers.common[
        CONTEXT_HEADER
      ];
    }, []);

  /**
   * Obtiene la primera ruta disponible para el usuario.
   */
  const getDefaultPath = React.useCallback(( navigationItems: NavModule[]): string | undefined => {
      const firstModule = navigationItems[0];
      if (!firstModule) {
        return undefined;
      }
      return (
        firstModule.vistas[0]?.pathRelativo ??
        firstModule.pathRelativo
      );
    },
    []
  );

  /**
   * Busca el módulo asociado a la ruta actual.
   */
  const findSelectedModule = React.useCallback(
    (
      navigationItems: NavModule[],
      pathname: string
    ): NavModule | undefined => {
      return navigationItems.find((module) => {
        const moduleMatches =
          pathname.startsWith(
            module.pathRelativo
          );

        const viewMatches = module.vistas.some(
          (view) =>
            pathname.startsWith(
              view.pathRelativo
            )
        );

        return moduleMatches || viewMatches;
      });
    },
    []
  );

  /**
   * Configura completamente una sesión autenticada.
   *
   * Esta función es utilizada tanto al seleccionar
   * un contexto como al restaurar una sesión existente.
   */
  const establishSession = React.useCallback(
    (
      session: IRespuestaContexto,
      options: EstablishSessionOptions = {}
    ): void => {
      const {
        persist = true,
        redirect = true,
        currentPath,
      } = options;

      if (persist) {
        const encryptedSession =
          encryptData(session);

        saveItem(
          STORAGE_KEY,
          encryptedSession
        );
      }

      configureApiContext(session);

      const authenticatedUser =
        buildUser(session);

      const navigationItems =
        buildNavigationFromPerms(
          session.permisos ?? []
        );

      const currentModule = currentPath
        ? findSelectedModule(
            navigationItems,
            currentPath
          )
        : undefined;

      const selectedModule =
        currentModule ?? navigationItems[0];

      setUser(authenticatedUser);
      setNavigation(navigationItems);
      setSelectedNav(selectedModule);

      if (!redirect) {
        return;
      }

      const defaultPath =
        getDefaultPath(navigationItems);

      navigate(defaultPath ?? "/401", {
        replace: true,
      });
    },
    [
      buildUser,
      configureApiContext,
      findSelectedModule,
      getDefaultPath,
      navigate,
      saveItem,
      setNavigation,
      setSelectedNav,
      setUser,
    ]
  );

  /**
   * Intenta recuperar una sesión almacenada.
   *
   * Retorna `true` cuando encuentra y restaura una sesión,
   * o `false` cuando no existe información almacenada.
   */
  const restoreSession =
    React.useCallback((): boolean => {
      const storedSession =
        getItem(STORAGE_KEY);

      if (!storedSession) {
        return false;
      }

    const decryptedSession: IRespuestaContexto =
  decryptData(storedSession);

      const isPublicRoute =
        location.pathname === "/" ||
        location.pathname.startsWith("/auth");

      establishSession(decryptedSession, {
        persist: false,
        redirect: isPublicRoute,
        currentPath: location.pathname,
      });

      return true;
    }, [
      establishSession,
      getItem,
      location.pathname,
    ]);

  /**
   * Elimina todos los datos relacionados con la sesión
   * y redirige al usuario a la página principal.
   */
  const clearSession =
    React.useCallback((): void => {
      deleteItem(STORAGE_KEY);
      clearApiContext();

      setUser(undefined);
      setNavigation([]);
      setSelectedNav(undefined);

      navigate("/", {
        replace: true,
      });
    }, [
      clearApiContext,
      deleteItem,
      navigate,
      setNavigation,
      setSelectedNav,
      setUser,
    ]);

  return {
    establishSession,
    restoreSession,
    clearSession,
  };
};

export default useSession;